import { Box, Divider, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import { useApplication } from 'src/context/ApplicationContext';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, FORM_FIELDS_TYPES, PLATZHALTER_TEXT } from 'src/lib/constants/constant';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, RadioButtonValidation } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkReadOnlyInUeberarbeitung, isTrue } from 'src/utils/helper';
import ApplicationHeading from '../../../../application-heading/ApplicationHeading';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const ProjektbeschreibungAntragStipendium: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	const gruendungErfolgt = get(
		application,
		P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Gruendung_Bereits_Erfolgt.path,
	);

	const gruendungsdatum = get(
		application,
		P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Unternehmensgruendung_Am.path,
	);
	const isGruendungsdatumNotInFuture = gruendungsdatum && new Date(gruendungsdatum) <= new Date();
	return (
		<>
			<ApplicationHeading title='Unternehmensstatus' description={PLATZHALTER_TEXT} />
			<PruefAssistenz
				statusPath={P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Pruefstatus.path}
				hinweisPath={P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Hinweis_Fuer_Den_Antragsteller.path}
				ueberarbeitetAmPath={P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Ueberarbeitet_Am.path}
				erledigtPath={P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Erledigt_Durch_Antragsteller.path}
				formStruc={formStruc(application)}
			/>
			<CreateForm
				formStruc={formStrucBeschreibung1(application)}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
			{isTrue(gruendungErfolgt) ?
				isGruendungsdatumNotInFuture && (
					<>
						<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', my: 3 }} />
						<Typography sx={{ fontWeight: 'bold', mb: 2 }}>Upload Nachweis der Unternehmensgründung</Typography>
						<Typography sx={{ mb: 1 }}>
							Bitte laden Sie hier einen Nachweis für das Datum der Unternehmensgründung (zum Beispiel Gewerbeanmeldung oder Anmeldung beim
							Finanzamt) als .pdf-Datei hoch.
						</Typography>
						<Typography>
							Wichtig: Der Nachweis ist Bestandteil des Förderantrags. Der Upload ist daher wichtig für die Einreichung Ihres vollständigen
							Antrags auf Förderung.
						</Typography>{' '}
						<Box sx={{ border: 1, borderColor: 'divider', borderRadius: '8px', px: 2, pb: 2, mt: 2 }}>
							<CreateForm
								formStruc={formStrucNachweis(application)}
								formValues={application}
								updateFormValuesFunc={setApplicationProperty}
								readOnly={updatedReadOnly}
							/>
						</Box>
					</>
				)
			:	<>
					<Typography sx={{ mt: 2 }}>
						<b>Wichtig:</b> Bitte reichen Sie den Nachweis der Unternehmensgründung nach (zum Beispiel Gewerbeanmeldung oder Anmeldung beim
						Finanzamt).
					</Typography>
					<Typography>
						<b>Eine Bewilligung der push!-Förderung kann nur für gegründete Unternehmen erfolgen</b>
					</Typography>
				</>
			}
			<Divider sx={{ borderBottomWidth: 1, borderColor: 'black', my: 3 }} />
			<CreateForm
				formStruc={formStrucBeschreibung2()}
				formValues={application}
				updateFormValuesFunc={setApplicationProperty}
				readOnly={updatedReadOnly}
			/>
		</>
	);
};

export default ProjektbeschreibungAntragStipendium;

const formStruc = (_application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Erledigt_Durch_Antragsteller,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];

	return struc;
};

const formStrucBeschreibung1 = (application: any) => {
	const gruendungErfolgt = get(
		application,
		P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Gruendung_Bereits_Erfolgt.path,
	);

	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Gruendung_Bereits_Erfolgt,
					type: FORM_FIELDS_TYPES.radio,
					label: 'Ist die Gründung bereits erfolgt?',
					validation: [{ validateFunction: RadioButtonValidation }],
				},
			],
		},
		...(isTrue(gruendungErfolgt) ?
			[
				{
					fields: [
						{
							...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Unternehmensgruendung_Am,
							label: 'Gründungsdatum',
						},
					],
					userProps: { direction: 'column', columnToDivide: 4 },
				},
			]
		:	[]),
	];
	return struc;
};

const formStrucNachweis = (application: any) => {
	let struc: FormStrucItem[] = [];
	const gruendungErfolgt = get(
		application,
		P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Gruendung_Bereits_Erfolgt.path,
	);

	const gruendungsdatum = get(
		application,
		P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Unternehmensgruendung_Am.path,
	);
	const isGruendungsdatumNotInFuture = gruendungsdatum && new Date(gruendungsdatum) <= new Date();

	if (isGruendungsdatumNotInFuture && isTrue(gruendungErfolgt)) {
		struc.push({
			fields: [
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Nachweis_Unternehmensgruendung_Bezeichnung,
				},
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Nachweis_Unternehmensgruendung_Datei,
				},
			],
			userProps: { direction: 'row', columnToDivide: 2 },
		});
	}
	return struc;
};

const formStrucBeschreibung2 = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Star_0070459294_ngen_Auf_Dem_Markt_Angeboten,
					type: FORM_FIELDS_TYPES.radio,
					validation: [{ validateFunction: RadioButtonValidation }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
		{
			fields: [
				{
					...P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Startup_Stipendium_Umsatz_Vorjahr_In_Euro,
					format: 'currency',
				},
				P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Projektbeschreibung_Star_0191006782_rechnung_Auf_Vollzeitstellen,
			],
		},
	];
	return struc;
};

export const getFormStrucProjektbeschreibungAntragStipendium = (application: any) => {
	let result = concat(formStrucBeschreibung1(application), formStrucBeschreibung2(), formStrucNachweis(application));
	if (
		checkIfPruefAssitenzNeedsToValidate(
			application,
			P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Pruefstatus.path,
			P.Projektbeschreibung_Startup_Stipendium.schema.attributes.Erledigt_Durch_Antragsteller.path,
		)
	) {
		result = concat(formStruc(application), result);
	}
	return result;
};
